(function () {
  const selectors = {
    section: "[data-gallery-section]",
    json: "[data-gallery-json]",
    grid: "[data-gallery-grid]",
    sentinel: "[data-gallery-sentinel]",
    loadMore: "[data-gallery-load-more]",
    pagination: "[data-gallery-pagination]",
    patternFilter: "[data-pattern-filter]",
    productFilter: "[data-product-filter]",
    empty: "[data-gallery-empty]",
    modal: "[data-gallery-modal]",
    activeFilters: "[data-gallery-active-filters]",
    controlsToggle: "[data-gallery-controls-toggle]",
    controlsWrapper: "[data-gallery-controls]",
  };

  class Gallery {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      const rawJson = section.querySelector(selectors.json)?.textContent;
      this.data = rawJson ? JSON.parse(rawJson) : {};
      this.items = (this.data.entries || []).map((item) => ({
        ...item,
        pattern_tags: Array.isArray(item.pattern_tags)
          ? item.pattern_tags
          : item.pattern_tags
          ? [item.pattern_tags]
          : [],
        product_tags: Array.isArray(item.product_tags)
          ? item.product_tags
          : item.product_tags
          ? [item.product_tags]
          : [],
      }));
      this.items.forEach((item, index) => {
        item._randomRank = Math.random();
        item._originalIndex = index;
      });

      this.patternFilter = section.querySelector(selectors.patternFilter);
      this.productFilter = section.querySelector(selectors.productFilter);
      this.grid = section.querySelector(selectors.grid);
      this.sentinel = section.querySelector(selectors.sentinel);
      this.loadMoreButton = section.querySelector(selectors.loadMore);
      this.paginationEl = section.querySelector(selectors.pagination);
      this.emptyState = section.querySelector(selectors.empty);
      this.modal = section.querySelector(selectors.modal);
      this.activeFilters = section.querySelector(selectors.activeFilters);
      this.controlsWrapper = section.querySelector(selectors.controlsWrapper);
      this.controlsToggle = section.querySelector(selectors.controlsToggle);
      this.mobileQuery = window.matchMedia("(max-width: 1023px)");

      this.paginationMode = this.data.paginationMode || "infinite";
      this.perPage = this.data.itemsPerPage || 12;
      this.visibleCount =
        this.paginationMode === "infinite" ? this.perPage : null;
      this.page = 1;
      this.activePatternTags = [];
      this.activeProductTags = [];
      this.modalIndex = 0;
      this.modalItems = [];

      if (!this.items.length) {
        this.renderEmptyState();
        return;
      }

      this.applyUrlParams();
      this.buildTagFilters();
      this.setCollapseState(this.mobileQuery.matches);
      this.attachFilterListeners();
      this.render();
      this.setupModal();
      this.setupInfiniteScroll();
    }

    applyUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const pattern = params.get("pattern");
      const product = params.get("product");

      this.activePatternTags = this.parseTagParam(pattern);
      this.activeProductTags = this.parseTagParam(product);
    }

    parseTagParam(value) {
      if (!value) return [];
      return value
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }

    buildTagFilters() {
      const patternTags = new Set();
      const productTags = new Set();

      this.items.forEach((item) => {
        (item.pattern_tags || []).forEach((tag) => patternTags.add(tag));
        (item.product_tags || []).forEach((tag) => productTags.add(tag));
      });

      this.renderTagPills(
        this.patternFilter,
        [...patternTags].sort(),
        "pattern"
      );
      this.renderTagPills(
        this.productFilter,
        [...productTags].sort(),
        "product"
      );
    }

    renderTagPills(container, tags, type) {
      if (!container) return;
      container.innerHTML = "";
      tags.forEach((tag) => {
        const label = document.createElement("label");
        label.className = "gallery-filter-pill";
        const input = document.createElement("input");
        input.type = "checkbox";
        input.value = tag;
        input.checked =
          type === "pattern"
            ? this.activePatternTags.includes(tag)
            : this.activeProductTags.includes(tag);
        const text = document.createElement("span");
        text.textContent = tag;
        label.append(input, text);
        container.append(label);

        input.addEventListener("change", () => {
          this.handleTagToggle(type, tag, input.checked);
        });
      });
    }

    toggleTag(list, tag, isChecked) {
      const idx = list.indexOf(tag);
      if (isChecked && idx === -1) list.push(tag);
      if (!isChecked && idx !== -1) list.splice(idx, 1);
    }

    attachFilterListeners() {
      this.controlsToggle?.addEventListener("click", () => {
        const expanded =
          this.controlsToggle.getAttribute("aria-expanded") === "true";
        this.setCollapseState(expanded);
      });

      this.mobileQuery?.addEventListener("change", (event) => {
        this.setCollapseState(event.matches);
      });

      this.loadMoreButton?.addEventListener("click", () => {
        this.loadMore();
      });
    }

    updateUrlParams() {
      const params = new URLSearchParams(window.location.search);
      this.setTagParam(params, "pattern", this.activePatternTags);
      this.setTagParam(params, "product", this.activeProductTags);

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, "", newUrl);
    }

    setTagParam(params, key, values) {
      if (values.length) {
        params.set(key, values.join(","));
      } else {
        params.delete(key);
      }
    }

    handleTagToggle(type, tag, isChecked) {
      const targetList =
        type === "pattern" ? this.activePatternTags : this.activeProductTags;
      this.toggleTag(targetList, tag, isChecked);
      this.page = 1;
      this.visibleCount =
        this.paginationMode === "infinite" ? this.perPage : this.visibleCount;
      this.updateUrlParams();
      this.buildTagFilters();
      this.renderActiveFilters();
      this.render();
    }

    setCollapseState(collapsed) {
      if (!this.controlsWrapper) return;
      this.controlsWrapper.classList.toggle("is-collapsed", collapsed);
      this.controlsToggle?.setAttribute("aria-expanded", String(!collapsed));
      this.renderActiveFilters();
    }

    getProcessedItems() {
      const matchesAnyTag = (activeTags, itemTags) =>
        activeTags.length === 0
          ? true
          : activeTags.some((tag) => itemTags.includes(tag));
      const getSortIndex = (item) =>
        typeof item.sort_index === "number" && Number.isFinite(item.sort_index)
          ? item.sort_index
          : 0;

      let items = this.items.filter((item) => {
        const matchPattern = matchesAnyTag(
          this.activePatternTags,
          item.pattern_tags
        );
        const matchProduct = matchesAnyTag(
          this.activeProductTags,
          item.product_tags
        );
        return matchPattern && matchProduct;
      });

      const order = "random";

      if (order === "newest") {
        items = items.sort((a, b) => getSortIndex(a) - getSortIndex(b));
      } else if (order === "oldest") {
        items = items.sort((a, b) => getSortIndex(b) - getSortIndex(a));
      } else {
        items = items.sort((a, b) => a._randomRank - b._randomRank);
      }

      return items;
    }

    renderActiveFilters() {
      if (!this.activeFilters) return;
      const isCollapsed = this.controlsWrapper?.classList.contains("is-collapsed");
      const tags = [...this.activePatternTags, ...this.activeProductTags];
      if (!tags.length || !isCollapsed) {
        this.activeFilters.classList.add("hidden");
        this.activeFilters.innerHTML = "";
        return;
      }

      this.activeFilters.classList.remove("hidden");
      this.activeFilters.innerHTML = "";

      tags.forEach((tag) => {
        const pill = document.createElement("label");
        pill.className = "gallery-filter-pill";
        const input = document.createElement("input");
        input.type = "checkbox";
        input.checked = true;
        const text = document.createElement("span");
        text.textContent = tag;
        pill.append(input, text);
        this.activeFilters.append(pill);

        input.addEventListener("change", () => {
          this.handleTagToggle(
            this.activePatternTags.includes(tag) ? "pattern" : "product",
            tag,
            input.checked
          );
        });
      });
    }

    shuffle(list) {
      const array = [...list];
      for (let i = array.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    }

    renderEmptyState() {
      if (this.emptyState) this.emptyState.hidden = false;
      this.section.querySelector(".gallery-controls")?.classList.add("hidden");
      this.grid.innerHTML = "";
      this.paginationEl?.classList.add("hidden");
      this.loadMoreButton?.classList.add("hidden");
      this.sentinel?.classList.add("hidden");
    }

    render() {
      this.processedItems = this.getProcessedItems();
      this.section
        .querySelector(".gallery-controls")
        ?.classList.remove("hidden");
      if (!this.processedItems.length) {
        this.grid.innerHTML = "";
        if (this.emptyState) this.emptyState.hidden = false;
        this.paginationEl?.classList.add("hidden");
        this.loadMoreButton?.classList.add("hidden");
        this.sentinel?.classList.add("hidden");
        return;
      }

      if (this.emptyState) this.emptyState.hidden = true;

      if (this.paginationMode === "infinite") {
        this.renderInfinite();
      } else {
        this.renderPaginated();
      }
    }

    renderInfinite() {
      const itemsToShow = this.processedItems.slice(
        0,
        this.visibleCount || this.perPage
      );
      this.renderCards(itemsToShow);

      const hasMore = itemsToShow.length < this.processedItems.length;
      this.loadMoreButton?.classList.toggle("hidden", !hasMore);
      this.sentinel?.classList.toggle("hidden", !hasMore);
      this.paginationEl?.classList.add("hidden");
    }

    renderPaginated() {
      const totalPages = Math.max(
        1,
        Math.ceil(this.processedItems.length / this.perPage)
      );
      if (this.page > totalPages) this.page = totalPages;
      const start = (this.page - 1) * this.perPage;
      const end = start + this.perPage;
      const itemsToShow = this.processedItems.slice(start, end);
      this.renderCards(itemsToShow);

      this.renderPagination(totalPages);
      this.loadMoreButton?.classList.add("hidden");
      this.sentinel?.classList.add("hidden");
    }

    renderPagination(totalPages) {
      if (!this.paginationEl) return;
      this.paginationEl.classList.remove("hidden");
      this.paginationEl.innerHTML = "";

      const prev = document.createElement("button");
      prev.textContent = "Previous";
      prev.disabled = this.page === 1;
      prev.addEventListener("click", () => {
        if (this.page > 1) {
          this.page -= 1;
          this.renderPaginated();
          this.scrollToTop();
        }
      });

      const next = document.createElement("button");
      next.textContent = "Next";
      next.disabled = this.page >= totalPages;
      next.addEventListener("click", () => {
        if (this.page < totalPages) {
          this.page += 1;
          this.renderPaginated();
          this.scrollToTop();
        }
      });

      const indicator = document.createElement("span");
      indicator.className = "page-indicator";
      indicator.textContent = `Page ${this.page} of ${totalPages}`;

      this.paginationEl.append(prev, indicator, next);
    }

    scrollToTop() {
      this.section.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    renderCards(items) {
      this.modalItems = items;
      this.grid.innerHTML = "";
      items.forEach((item, index) => {
        const card = this.createCard(item, index);
        this.grid.append(card);
      });
    }

    createCard(item, index) {
      const card = document.createElement("article");
      card.className = "gallery-card";
      card.tabIndex = 0;
      card.dataset.index = index;

      const configurator = this.createConfiguratorLink(item);
      if (configurator) card.append(configurator);

      const img = document.createElement("img");
      const thumbSrc =
        (item.image && (item.image.default || item.image.small || item.image.large)) || "";
      img.src = thumbSrc;
      if (item.image?.srcset) {
        img.srcset = item.image.srcset;
      }
      if (item.image?.sizes) {
        img.sizes = item.image.sizes;
      }
      img.loading = "lazy";
      img.decoding = "async";
      img.alt = item.alt || item.title || "";
      card.append(img);

      const title = document.createElement("p");
      title.className = "gallery-card-title";
      title.textContent = item.title;
      card.append(title);

      card.addEventListener("click", (event) => {
        const target = event.target;
        if (target.closest(".gallery-configurator")) return;
        this.openModal(index);
      });

      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.openModal(index);
        }
      });

      return card;
    }

    createConfiguratorLink(item) {
      if (!item.configurator_link) return null;
      const wrapper = document.createElement("div");
      wrapper.className = "gallery-configurator";
      const link = document.createElement("a");
      link.href = item.configurator_link;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      const label = this.data.configuratorLabel || "Open configurator";
      const icon = this.data.configuratorIcon || "";
      link.innerHTML = `${icon ? `<span class="gallery-configurator-icon">${icon}</span>` : ""}<span class="gallery-configurator-label">${label}</span>`;
      wrapper.append(link);
      link.addEventListener("click", (event) => event.stopPropagation());
      return wrapper;
    }

    loadMore() {
      if (this.visibleCount === null) return;
      const next = Math.min(
        this.visibleCount + this.perPage,
        this.processedItems.length
      );
      this.visibleCount = next;
      this.renderInfinite();
    }

    setupInfiniteScroll() {
      if (this.paginationMode !== "infinite" || !this.sentinel) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadMore();
        });
      });
      observer.observe(this.sentinel);
    }

    setupModal() {
      if (!this.modal) return;
      this.modalImage = this.modal.querySelector("[data-gallery-modal-image]");
      this.modalTitle = this.modal.querySelector("[data-gallery-modal-title]");
      this.modalTags = this.modal.querySelector("[data-gallery-modal-tags]");
      this.modalConfigurator = this.modal.querySelector(
        "[data-gallery-modal-configurator]"
      );
      this.modalCloseButtons = [
        ...this.modal.querySelectorAll("[data-gallery-modal-close]"),
      ];
      this.modalPrev = this.modal.querySelector("[data-gallery-modal-prev]");
      this.modalNext = this.modal.querySelector("[data-gallery-modal-next]");

      this.modalCloseButtons.forEach((btn) =>
        btn.addEventListener("click", (event) => {
          event.preventDefault();
          this.closeModal();
        })
      );
      this.modal.addEventListener("click", (event) => {
        const clickedImage = event.target.closest("[data-gallery-modal-image]");
        const clickedButton = event.target.closest(
          ".gallery-modal-close, .gallery-modal-configurator-btn, .gallery-modal-nav-btn"
        );
        const clickedInfo = event.target.closest(".gallery-modal-info");
        if (!clickedImage && !clickedButton && !clickedInfo) this.closeModal();
      });

      this.modalPrev?.addEventListener("click", (event) => {
        event.stopPropagation();
        this.navigateModal(-1);
      });

      this.modalNext?.addEventListener("click", (event) => {
        event.stopPropagation();
        this.navigateModal(1);
      });

      document.addEventListener("keydown", (event) => {
        if (!this.modal.classList.contains("active")) return;
        if (event.key === "Escape") this.closeModal();
        if (event.key === "ArrowLeft") this.navigateModal(-1);
        if (event.key === "ArrowRight") this.navigateModal(1);
      });

      this.modal.addEventListener("touchstart", (event) => {
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      });

      this.modal.addEventListener("touchend", (event) => {
        if (this.touchStartX === null || this.touchStartY === null) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const threshold = 40;
        if (absX > absY && absX > threshold) {
          if (deltaX > 0) {
            this.navigateModal(-1);
          } else {
            this.navigateModal(1);
          }
        }
        this.resetTouchState();
      });
    }

    openModal(index) {
      if (!this.modal || !this.modalItems.length) return;
      this.modalIndex = index;
      this.updateModalContent();
      this.modal.classList.add("active");
      document.body.style.overflow = "hidden";
      this.resetTouchState();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove("active");
      document.body.style.overflow = "";
    }

    navigateModal(step) {
      if (!this.modalItems.length) return;
      this.modalIndex =
        (this.modalIndex + step + this.modalItems.length) %
        this.modalItems.length;
      this.updateModalContent();
    }

    resetTouchState() {
      this.touchStartX = null;
      this.touchStartY = null;
    }

    updateModalContent() {
      const item = this.modalItems[this.modalIndex];
      if (!item) return;
      if (this.modalImage) {
        this.modalImage.src =
          item.image.modal ||
          item.image.default ||
          item.image.large ||
          item.image.small ||
          "";
        this.modalImage.removeAttribute("srcset");
        this.modalImage.removeAttribute("sizes");
        this.modalImage.alt = item.alt || item.title || "";
      }
      if (this.modalTitle) this.modalTitle.textContent = item.title;

      if (this.modalTags) {
        this.modalTags.innerHTML = "";
        [...(item.pattern_tags || []), ...(item.product_tags || [])].forEach(
          (tag) => {
            const li = document.createElement("li");
            li.className = "gallery-tag";
            li.textContent = tag;
            this.modalTags.append(li);
          }
        );
      }

      if (this.modalConfigurator) {
        if (item.configurator_link) {
          this.modalConfigurator.href = item.configurator_link;
          this.modalConfigurator.classList.remove("hidden");
        } else {
          this.modalConfigurator.classList.add("hidden");
        }
      }
    }
  }

  function initGallerySections() {
    document.querySelectorAll(selectors.section).forEach((section) => {
      new Gallery(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initGallerySections);
  } else {
    initGallerySections();
  }
})();
